/**
 * Region detection utilities
 * 
 * Provides utilities for normalizing country codes, validating regions,
 * and converting between different region representations.
 */

import { Region, DEFAULT_REGION } from '../types/region';
import {
  getMarketConfig,
  getMarketConfigForCountryCode,
  type MarketFeatureFlagState,
} from '../constants/markets';

/**
 * Normalize a country code to a Region
 * 
 * Converts ISO 3166-1 alpha-2 country codes (e.g., "US", "AU") to our Region type.
 * Returns null if the country code is not supported.
 * 
 * @param countryCode - ISO country code (case-insensitive)
 * @returns Region or null if not supported
 * 
 * @example
 * normalizeRegionCode('US') // 'US'
 * normalizeRegionCode('au') // 'AU'
 * normalizeRegionCode('GB') // 'GB'
 */
export function normalizeRegionCode(
  countryCode: string,
  featureFlags: MarketFeatureFlagState = {},
): Region | null {
  if (!countryCode || typeof countryCode !== 'string') {
    return null;
  }

  return getMarketConfigForCountryCode(countryCode, featureFlags)?.region ?? null;
}

/**
 * Type guard to check if a string is a valid Region
 * 
 * @param region - String to check
 * @returns True if region is a valid Region type
 * 
 * @example
 * isValidRegion('US') // true
 * isValidRegion('AU') // true
 * isValidRegion('GB') // true
 */
export function isValidRegion(
  region: string,
  featureFlags: MarketFeatureFlagState = {},
): region is Region {
  if (!region || typeof region !== 'string') {
    return false;
  }

  const trimmed = region.trim();
  if (trimmed !== trimmed.toUpperCase()) {
    return false;
  }

  const normalized = trimmed as Region;
  return getMarketConfig(normalized, featureFlags) !== null;
}

/**
 * Get the default region
 * 
 * @returns Default region (US)
 */
export function getDefaultRegion(): Region {
  return DEFAULT_REGION;
}

/**
 * Validate and normalize a region string
 * 
 * Attempts to normalize the input to a valid Region.
 * Returns the default region if normalization fails.
 * 
 * @param region - Region string to validate
 * @returns Valid Region
 * 
 * @example
 * validateRegion('US') // 'US'
 * validateRegion('us') // 'US'
 * validateRegion('invalid') // 'US' (default)
 */
export function validateRegion(
  region: string | null | undefined,
  featureFlags: MarketFeatureFlagState = {},
): Region {
  if (!region) {
    return getDefaultRegion();
  }

  const normalized = region.toUpperCase().trim();
  if (isValidRegion(normalized, featureFlags)) {
    return normalized as Region;
  }

  // Try to normalize as country code
  const fromCountryCode = normalizeRegionCode(normalized, featureFlags);
  if (fromCountryCode) {
    return fromCountryCode;
  }

  return getDefaultRegion();
}

/**
 * Get region from country code with fallback
 * 
 * Attempts to normalize the country code to a Region.
 * Returns the default region if the country code is not supported.
 * 
 * @param countryCode - ISO country code
 * @returns Region (never null)
 */
export function getRegionFromCountryCode(
  countryCode: string | null | undefined,
  featureFlags: MarketFeatureFlagState = {},
): Region {
  if (!countryCode) {
    return getDefaultRegion();
  }

  const normalized = normalizeRegionCode(countryCode, featureFlags);
  return normalized || getDefaultRegion();
}

/**
 * Map country code to region for product display
 * 
 * Maps country codes to regions:
 * - AU (Australia) → AU (AUD products)
 * - Everything else → US (USD products)
 * 
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns Region (AU or US)
 */
export function mapCountryCodeToRegion(
  countryCode: string | null | undefined,
  featureFlags: MarketFeatureFlagState = {},
): Region {
  if (!countryCode) {
    return getDefaultRegion();
  }

  const market = getMarketConfigForCountryCode(countryCode, featureFlags);
  return market?.region ?? getDefaultRegion();
}
